import type React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { useIsMobile, useIncidentSync } from 'hooks';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Input,
  Dropdown,
  Option,
  Tooltip,
  mergeClasses,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  SearchRegular,
  ArrowSortDownRegular,
  ArrowSortUpRegular,
  FilterRegular,
  InfoRegular,
  PersonRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { Incident, StatusPanelSettings } from 'types';
import { IncidentSeverity, IncidentState } from 'types';
import { extractApiErrorMessage, updateIncidentSeverity } from 'services';
import { useToast } from 'contexts';
import { SeverityBadge } from './SeverityBadge';
import { SlaCountdown } from './SlaCountdown';
import { IncidentActions } from './IncidentActions';
import { IncidentTagBadges } from './IncidentTagBadges';
import { UserPickerDialog } from './UserPickerDialog';
import { useIncidentTableStyles, useIncidentTableToolbarStyles, useGroupHeaderStyles, useMobileCardStyles } from './IncidentTable.styles';

type SortField = 'created' | 'sla';
type SortDirection = 'asc' | 'desc';
type StatusFilterValue = '__open__' | '__all__' | 'new' | 'active' | 'closed';

interface IncidentTableProps {
  incidents: Incident[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  settings: StatusPanelSettings;
  isWallScreen?: boolean;
}

const ALL_VALUE = '__all__';
const UNASSIGNED_KEY = '__unassigned__';

// Severity order: High (3) → Medium (2) → Low (1) → Informational (0)
const SEVERITY_ORDER = [
  IncidentSeverity.High,
  IncidentSeverity.Medium,
  IncidentSeverity.Low,
  IncidentSeverity.Informational,
];

interface GroupedIncidents {
  ownerKey: string;
  ownerName: string;
  severityGroups: {
    severity: IncidentSeverity;
    incidents: Incident[];
  }[];
}

/**
 * Checks if an owner value is effectively empty (null, undefined, or empty/whitespace string).
 */
// ── Mobile card component ────────────────────────────────────────────────────

interface IncidentMobileCardProps {
  incident: Incident;
  settings: StatusPanelSettings;
  onRefresh: () => void;
  onAssignClick: (incident: Incident) => void;
  onSeverityChange: (incident: Incident, severity: IncidentSeverity) => Promise<void>;
  isUpdating: boolean;
}

const IncidentMobileCard: React.FC<IncidentMobileCardProps> = ({
  incident,
  settings,
  onRefresh,
  onAssignClick,
  onSeverityChange,
  isUpdating,
}) => {
  const cardStyles = useMobileCardStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={cardStyles.card}>
      {/* Top row: severity badge + SLA countdown */}
      <div className={cardStyles.cardTop}>
        <SeverityBadge severity={incident.severity} size="medium" />
        <SlaCountdown
          slaDeadline={incident.slaDeadline}
          severity={incident.severity}
          incidentState={incident.state}
          slaType={incident.slaType}
          size="medium"
        />
      </div>

      {/* Owner */}
      <Text size={300} className={cardStyles.cardOwner}>
        {incident.assignedOperatorName ?? t('statusPanel.grouping.unassigned')}
      </Text>

      {/* Actions row + expand toggle */}
      <div className={cardStyles.cardActions}>
        <IncidentActions
          incident={incident}
          settings={settings}
          onActionComplete={onRefresh}
          onAssignClick={onAssignClick}
          size="medium"
        />
        <Button
          appearance="subtle"
          size="small"
          icon={expanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        />
      </div>

      <div className={cardStyles.cardDetails}>
        <div className={cardStyles.cardDetailRow}>
          <Text size={200} className={cardStyles.cardDetailLabel}>{t('statusPanel.columns.severity')}</Text>
          <div className={cardStyles.editableControlRow}>
            <Dropdown
              className={cardStyles.mobileEditableDropdown}
              disabled={isUpdating}
              value={t(`statusPanel.severity.${incident.severity}`)}
              onOptionSelect={(_, data) => {
                const value = Number(data.optionValue);
                if (Number.isInteger(value) && value !== incident.severity) {
                  void onSeverityChange(incident, value as IncidentSeverity);
                }
              }}
            >
              <Option value={String(IncidentSeverity.High)}>{t('statusPanel.severity.3')}</Option>
              <Option value={String(IncidentSeverity.Medium)}>{t('statusPanel.severity.2')}</Option>
              <Option value={String(IncidentSeverity.Low)}>{t('statusPanel.severity.1')}</Option>
              <Option value={String(IncidentSeverity.Informational)}>{t('statusPanel.severity.0')}</Option>
            </Dropdown>
            {isUpdating && <Spinner size="tiny" className={cardStyles.savingSpinner} />}
          </div>
        </div>

        <div className={cardStyles.cardDetailRow}>
          <Text size={200} className={cardStyles.cardDetailLabel}>{t('statusPanel.columns.state')}</Text>
          <Text size={200} className={cardStyles.cardDetailValue}>
            {t(`statusPanel.statusSimple.${getSimpleStatusForIncident(incident)}`)}
          </Text>
        </div>
      </div>

      {/* Expandable detail section */}
      {expanded && (
        <div className={cardStyles.cardDetails}>
          <div className={cardStyles.cardDetailRow}>
            <Text size={200} className={cardStyles.cardDetailLabel}>{t('statusPanel.columns.title')}</Text>
            <Text size={200} className={cardStyles.cardDetailValue}>{incident.title}</Text>
            <IncidentTagBadges tags={incident.tags} />
          </div>
          <div className={cardStyles.cardDetailRow}>
            <Text size={200} className={cardStyles.cardDetailLabel}>{t('statusPanel.columns.customer')}</Text>
            <Text size={200} className={cardStyles.cardDetailValue}>{incident.customerId}</Text>
          </div>
          <div className={cardStyles.cardDetailRow}>
            <Text size={200} className={cardStyles.cardDetailLabel}>{t('statusPanel.columns.created')}</Text>
            <Text size={200} className={cardStyles.cardDetailValue}>
              {new Date(incident.createdTime).toLocaleString()}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Checks if an owner value is effectively empty (null, undefined, or empty/whitespace string).
 */
const isOwnerEmpty = (owner: string | null | undefined): boolean => {
  return owner === null || owner === undefined || owner.trim() === '';
};

const getSimpleStatusForIncident = (incident: Incident): 'new' | 'active' | 'closed' => {
  if (incident.state === IncidentState.New) {
    return 'new';
  }

  if (incident.state === IncidentState.Closed) {
    return 'closed';
  }

  return 'active';
};

/**
 * Groups incidents by owner, then by severity within each owner.
 * Unassigned incidents come first, then owners sorted alphabetically.
 * Within each owner, severities are ordered High → Medium → Low → Informational.
 */
const groupIncidentsByOwnerAndSeverity = (incidents: Incident[]): GroupedIncidents[] => {
  // Group by owner
  const byOwner = new Map<string, Incident[]>();

  for (const incident of incidents) {
    // Treat null, undefined, empty string, and whitespace-only as unassigned
    const ownerKey = isOwnerEmpty(incident.assignedOperatorName) ? UNASSIGNED_KEY : incident.assignedOperatorName!;
    const group = byOwner.get(ownerKey) ?? [];
    group.push(incident);
    byOwner.set(ownerKey, group);
  }

  // Sort owner keys: unassigned first, then alphabetically
  const sortedOwnerKeys = [...byOwner.keys()].sort((a, b) => {
    if (a === UNASSIGNED_KEY) return -1;
    if (b === UNASSIGNED_KEY) return 1;
    return a.localeCompare(b);
  });

  // Build grouped structure
  return sortedOwnerKeys.map((ownerKey) => {
    const ownerIncidents = byOwner.get(ownerKey) ?? [];

    // Group by severity within this owner
    const bySeverity = new Map<IncidentSeverity, Incident[]>();
    for (const incident of ownerIncidents) {
      const severityGroup = bySeverity.get(incident.severity) ?? [];
      severityGroup.push(incident);
      bySeverity.set(incident.severity, severityGroup);
    }

    // Order severities High → Medium → Low → Informational
    const severityGroups = SEVERITY_ORDER
      .filter((sev) => bySeverity.has(sev))
      .map((severity) => ({
        severity,
        incidents: bySeverity.get(severity) ?? [],
      }));

    return {
      ownerKey,
      ownerName: ownerKey === UNASSIGNED_KEY ? '' : ownerKey,
      severityGroups,
    };
  });
};

export const IncidentTable: React.FC<IncidentTableProps> = ({
  incidents,
  isLoading,
  error,
  onRefresh,
  settings,
  isWallScreen = false,
}) => {
  const styles = useIncidentTableStyles();
  const toolbarStyles = useIncidentTableToolbarStyles();
  const groupStyles = useGroupHeaderStyles();
    const isMobile = useIsMobile();
    const mobileStyles = useMobileCardStyles();
  const { t } = useTranslation();
  const waitForIncidentSync = useIncidentSync();
  const { showToast } = useToast();

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>(ALL_VALUE);
  const [customerFilter, setCustomerFilter] = useState<string>(ALL_VALUE);
  const [ownerFilter, setOwnerFilter] = useState<string>(ALL_VALUE);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('__open__');
  const [updatingIncidentIds, setUpdatingIncidentIds] = useState<Record<string, boolean>>({});

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // User picker dialog state
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [selectedIncidentForAssign, setSelectedIncidentForAssign] = useState<Incident | null>(null);

  // Handle assign click - open user picker dialog
  const handleAssignClick = useCallback((incident: Incident) => {
    setSelectedIncidentForAssign(incident);
    setUserPickerOpen(true);
  }, []);

  const handleUserPickerClose = useCallback(() => {
    setUserPickerOpen(false);
    setSelectedIncidentForAssign(null);
  }, []);

  const handleAssignComplete = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const withIncidentUpdate = useCallback(async (incidentId: string, action: () => Promise<void>) => {
    setUpdatingIncidentIds((prev) => ({ ...prev, [incidentId]: true }));
    try {
      await action();
    } finally {
      setUpdatingIncidentIds((prev) => ({ ...prev, [incidentId]: false }));
    }
  }, []);

  const handleSeverityChange = useCallback(async (incident: Incident, severity: IncidentSeverity) => {
    try {
      await withIncidentUpdate(incident.incidentId, async () => {
        await updateIncidentSeverity(incident.incidentId, severity);
        await waitForIncidentSync({
          incidentId: incident.incidentId,
          predicate: (message) => {
            const updatedSeverity = message.incident?.severity ?? message.severity;
            return updatedSeverity === severity;
          },
        });
      });

      showToast({
        title: t('statusPanel.sync.updateConfirmedTitle'),
        body: t('statusPanel.sync.updateConfirmedBody'),
        intent: 'success',
        timeout: 3000,
      });
    } catch (err) {
      showToast({
        title: t('statusPanel.errors.actionFailed', { action: t('statusPanel.columns.severity') }),
        body: extractApiErrorMessage(err, t('statusPanel.errors.unknownError')),
        intent: 'error',
        timeout: 8000,
      });
    }
  }, [showToast, t, waitForIncidentSync, withIncidentUpdate]);

  // Extract unique values for dropdowns
  const uniqueOwners = useMemo(() => {
    const unique = [...new Set(incidents.map((i) => i.assignedOperatorName).filter((name) => !isOwnerEmpty(name)))] as string[];
    return unique.sort();
  }, [incidents]);

  const uniqueCustomers = useMemo(() => {
    const unique = [...new Set(incidents.map((i) => i.customerId).filter((customerId) => customerId.trim() !== ''))];
    return unique.sort();
  }, [incidents]);

  // Toggle sort
  const handleSortToggle = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  // Apply filters and sorting
  const filteredAndSortedIncidents = useMemo(() => {
    // Deduplicate by incidentId
    const seen = new Set<string>();
    let result = incidents.filter((i) => {
      if (seen.has(i.incidentId)) {
        return false;
      }
      seen.add(i.incidentId);
      return true;
    });

    // Search filter (title)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((i) => i.title.toLowerCase().includes(query));
    }

    // Severity filter
    if (severityFilter !== ALL_VALUE) {
      const severityValue = Number(severityFilter);
      result = result.filter((i) => i.severity === severityValue);
    }

    // Owner filter
    if (ownerFilter !== ALL_VALUE) {
      if (ownerFilter === '__unassigned__') {
        result = result.filter((i) => isOwnerEmpty(i.assignedOperatorName));
      } else {
        result = result.filter((i) => i.assignedOperatorName === ownerFilter);
      }
    }

    // Customer filter
    if (customerFilter !== ALL_VALUE) {
      result = result.filter((i) => i.customerId === customerFilter);
    }

    // Status filter
    if (statusFilter === '__open__') {
      result = result.filter((i) => i.state !== IncidentState.Closed);
    } else if (statusFilter !== '__all__') {
      result = result.filter((i) => getSimpleStatusForIncident(i) === statusFilter);
    }

    // Sorting
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'created') {
        comparison = new Date(a.createdTime).getTime() - new Date(b.createdTime).getTime();
      } else if (sortField === 'sla') {
        comparison = new Date(a.slaDeadline).getTime() - new Date(b.slaDeadline).getTime();
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [incidents, searchQuery, severityFilter, customerFilter, ownerFilter, statusFilter, sortField, sortDirection]);

  const groupedIncidents = useMemo(
    () => groupIncidentsByOwnerAndSeverity(filteredAndSortedIncidents),
    [filteredAndSortedIncidents],
  );

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowSortUpRegular /> : <ArrowSortDownRegular />;
  };

  const renderHeaderLabel = (
    label: string,
    tooltip: string,
    sortIcon?: React.ReactNode,
  ) => (
    <span className={styles.headerLabelWithInfo}>
      <span>
        {label}
        {sortIcon ? <> {sortIcon}</> : null}
      </span>
      <Tooltip content={tooltip} relationship="description">
        <Button
          appearance="transparent"
          size="small"
          icon={<InfoRegular />}
          aria-label={tooltip}
          className={styles.headerInfoButton}
          onClick={(event) => event.stopPropagation()}
        />
      </Tooltip>
    </span>
  );

  if (isLoading && incidents.length === 0) {
    return <Spinner label={t('common.loading')} size={isWallScreen ? 'large' : 'medium'} />;
  }

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {error}
          <Button
            appearance="transparent"
            icon={<ArrowClockwiseRegular />}
            onClick={onRefresh}
          >
            {t('common.retry')}
          </Button>
        </MessageBarBody>
      </MessageBar>
    );
  }

  const tableSize = isWallScreen ? 'medium' : 'small';
  const textSize = isWallScreen ? 400 : 200;

  // Non-wall-screen mode with filters and sorting
  if (!isWallScreen) {
    return (
      <div className={styles.container}>
        {/* Toolbar with search and filters */}
        <div className={toolbarStyles.toolbar}>
          <div className={toolbarStyles.searchBox}>
            <Input
              contentBefore={<SearchRegular />}
              placeholder={t('statusPanel.filters.searchTitle')}
              value={searchQuery}
              onChange={(_, data) => setSearchQuery(data.value)}
              className={toolbarStyles.searchInput}
            />
          </div>
          <div className={toolbarStyles.filters}>
            <div className={toolbarStyles.filterGroup}>
              <FilterRegular className={toolbarStyles.filterIcon} />
              <Dropdown
                placeholder={t('statusPanel.filters.severity')}
                value={severityFilter === ALL_VALUE ? t('statusPanel.filters.allSeverities') : t(`statusPanel.severity.${severityFilter}`)}
                onOptionSelect={(_, data) => setSeverityFilter(data.optionValue ?? ALL_VALUE)}
                className={toolbarStyles.filterDropdown}
              >
                <Option value={ALL_VALUE}>{t('statusPanel.filters.allSeverities')}</Option>
                <Option value={String(IncidentSeverity.High)}>{t('statusPanel.severity.3')}</Option>
                <Option value={String(IncidentSeverity.Medium)}>{t('statusPanel.severity.2')}</Option>
                <Option value={String(IncidentSeverity.Low)}>{t('statusPanel.severity.1')}</Option>
                <Option value={String(IncidentSeverity.Informational)}>{t('statusPanel.severity.0')}</Option>
              </Dropdown>
            </div>
            <Dropdown
              placeholder={t('statusPanel.filters.customer')}
              value={
                customerFilter === ALL_VALUE
                  ? t('statusPanel.filters.allCustomers')
                  : customerFilter
              }
              onOptionSelect={(_, data) => setCustomerFilter(data.optionValue ?? ALL_VALUE)}
              className={toolbarStyles.filterDropdown}
            >
              <Option value={ALL_VALUE}>{t('statusPanel.filters.allCustomers')}</Option>
              {uniqueCustomers.map((customerId) => (
                <Option key={customerId} value={customerId}>{customerId}</Option>
              ))}
            </Dropdown>
            <Dropdown
              placeholder={t('statusPanel.filters.status')}
              value={
                statusFilter === '__open__'
                  ? t('statusPanel.filters.openOnly')
                  : statusFilter === '__all__'
                    ? t('statusPanel.filters.allStatuses')
                    : t(`statusPanel.statusSimple.${statusFilter}`)
              }
              onOptionSelect={(_, data) => setStatusFilter((data.optionValue as StatusFilterValue) ?? '__open__')}
              className={toolbarStyles.filterDropdown}
            >
              <Option value="__open__">{t('statusPanel.filters.openOnly')}</Option>
              <Option value="__all__">{t('statusPanel.filters.allStatuses')}</Option>
              <Option value="new">{t('statusPanel.statusSimple.new')}</Option>
              <Option value="active">{t('statusPanel.statusSimple.active')}</Option>
              <Option value="closed">{t('statusPanel.statusSimple.closed')}</Option>
            </Dropdown>
            <Dropdown
              placeholder={t('statusPanel.filters.owner')}
              value={
                ownerFilter === ALL_VALUE
                  ? t('statusPanel.filters.allOwners')
                  : ownerFilter === '__unassigned__'
                    ? t('statusPanel.filters.unassigned')
                    : ownerFilter
              }
              onOptionSelect={(_, data) => setOwnerFilter(data.optionValue ?? ALL_VALUE)}
              className={toolbarStyles.filterDropdown}
            >
              <Option value={ALL_VALUE}>{t('statusPanel.filters.allOwners')}</Option>
              <Option value="__unassigned__">{t('statusPanel.filters.unassigned')}</Option>
              {uniqueOwners.map((owner) => (
                <Option key={owner} value={owner}>{owner}</Option>
              ))}
            </Dropdown>
          </div>
        </div>

        {filteredAndSortedIncidents.length === 0 ? (
          <Text align="center" block size={400} className={toolbarStyles.noResults}>
            {incidents.length === 0 ? t('statusPanel.noIncidents') : t('statusPanel.filters.noMatches')}
          </Text>
        ) : isMobile ? (
          // Mobile: flat card list
          <div className={mobileStyles.mobileList}>
            {filteredAndSortedIncidents.map((incident) => (
              <IncidentMobileCard
                key={incident.incidentId}
                incident={incident}
                settings={settings}
                onRefresh={onRefresh}
                onAssignClick={handleAssignClick}
                onSeverityChange={handleSeverityChange}
                isUpdating={Boolean(updatingIncidentIds[incident.incidentId])}
              />
            ))}
          </div>
        ) : (
          <Accordion multiple collapsible defaultOpenItems={groupedIncidents.map(g => g.ownerKey)}>
            {/* Group incidents by owner > severity */}
            {groupedIncidents.map(({ ownerKey, ownerName, severityGroups }) => (
              <AccordionItem key={ownerKey} value={ownerKey} className={groupStyles.ownerSection}>
                {/* Owner Header */}
                <AccordionHeader
                  expandIconPosition="start"
                  className={groupStyles.ownerHeader}
                >
                  <div className={groupStyles.ownerHeaderContent}>
                    <PersonRegular className={groupStyles.ownerIcon} />
                    <Text weight="semibold" size={400}>
                      {ownerKey === UNASSIGNED_KEY
                        ? t('statusPanel.grouping.unassigned')
                        : ownerName}
                    </Text>
                    <Text className={groupStyles.incidentCount} size={200}>
                      ({severityGroups.reduce((sum, g) => sum + g.incidents.length, 0)} {t('statusPanel.grouping.incidents')})
                    </Text>
                  </div>
                </AccordionHeader>
                <AccordionPanel className={groupStyles.ownerPanel}>
                  {/* Severity sub-groups */}
                  <Accordion multiple collapsible defaultOpenItems={severityGroups.map(sg => `${ownerKey}-${sg.severity}`)}>
                    {severityGroups.map(({ severity, incidents: sevIncidents }) => (
                      <AccordionItem key={`${ownerKey}-${severity}`} value={`${ownerKey}-${severity}`} className={groupStyles.severitySection}>
                        {/* Severity Header */}
                        <AccordionHeader
                          expandIconPosition="start"
                          className={groupStyles.severityHeader}
                        >
                          <div className={groupStyles.severityHeaderContent}>
                            <SeverityBadge severity={severity} size="medium" />
                            <Text size={200} className={groupStyles.severityCount}>
                              ({sevIncidents.length})
                            </Text>
                          </div>
                        </AccordionHeader>
                        <AccordionPanel>
                          {/* Incident Table for this severity group */}
                          <div className={styles.tableScrollHost}>
                            <Table
                              aria-label={`${ownerName} ${t(`statusPanel.severity.${severity}`)} incidents`}
                              size={tableSize}
                              className={styles.tableBase}
                            >
                              <TableHeader>
                                <TableRow>
                                  <TableHeaderCell>
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.title'),
                                      t('statusPanel.columnTooltips.title'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell>
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.tags'),
                                      t('statusPanel.columnTooltips.tags'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell>
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.customer'),
                                      t('statusPanel.columnTooltips.customer'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell>
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.state'),
                                      t('statusPanel.columnTooltips.state'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell>
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.severity'),
                                      t('statusPanel.columnTooltips.severity'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell
                                    className={toolbarStyles.sortableHeader}
                                    onClick={() => handleSortToggle('created')}
                                  >
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.created'),
                                      t('statusPanel.columnTooltips.created'),
                                      getSortIcon('created'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell
                                    className={toolbarStyles.sortableHeader}
                                    onClick={() => handleSortToggle('sla')}
                                  >
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.sla'),
                                      t('statusPanel.columnTooltips.sla'),
                                      getSortIcon('sla'),
                                    )}
                                  </TableHeaderCell>
                                  <TableHeaderCell>
                                    {renderHeaderLabel(
                                      t('statusPanel.columns.actions'),
                                      t('statusPanel.columnTooltips.actions'),
                                    )}
                                  </TableHeaderCell>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sevIncidents.map((incident) => (
                                  <TableRow key={incident.incidentId}>
                                    <TableCell>
                                      <TableCellLayout truncate>
                                        <Text size={textSize}>{incident.title}</Text>
                                      </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                      <IncidentTagBadges tags={incident.tags} />
                                    </TableCell>
                                    <TableCell>
                                      <Text size={textSize}>{incident.customerId}</Text>
                                    </TableCell>
                                    <TableCell className={styles.editableCell}>
                                      <Text size={textSize}>{t(`statusPanel.statusSimple.${getSimpleStatusForIncident(incident)}`)}</Text>
                                    </TableCell>
                                    <TableCell className={styles.editableCell}>
                                      <div className={styles.editableControlRow}>
                                        <Dropdown
                                          className={styles.compactEditableDropdown}
                                          disabled={Boolean(updatingIncidentIds[incident.incidentId])}
                                          value={t(`statusPanel.severity.${incident.severity}`)}
                                          onOptionSelect={(_, data) => {
                                            const value = Number(data.optionValue);
                                            if (Number.isInteger(value) && value !== incident.severity) {
                                              void handleSeverityChange(incident, value as IncidentSeverity);
                                            }
                                          }}
                                        >
                                          <Option value={String(IncidentSeverity.High)}>{t('statusPanel.severity.3')}</Option>
                                          <Option value={String(IncidentSeverity.Medium)}>{t('statusPanel.severity.2')}</Option>
                                          <Option value={String(IncidentSeverity.Low)}>{t('statusPanel.severity.1')}</Option>
                                          <Option value={String(IncidentSeverity.Informational)}>{t('statusPanel.severity.0')}</Option>
                                        </Dropdown>
                                        {Boolean(updatingIncidentIds[incident.incidentId]) && <Spinner size="tiny" className={styles.savingSpinner} />}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Text size={textSize}>
                                        {new Date(incident.createdTime).toLocaleString()}
                                      </Text>
                                    </TableCell>
                                    <TableCell>
                                      <SlaCountdown
                                        slaDeadline={incident.slaDeadline}
                                        severity={incident.severity}
                                        incidentState={incident.state}
                                        slaType={incident.slaType}
                                        size="medium"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <IncidentActions
                                        incident={incident}
                                        settings={settings}
                                        onActionComplete={onRefresh}
                                        onAssignClick={handleAssignClick}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {/* User Picker Dialog for assigning incidents */}
        <UserPickerDialog
          incident={selectedIncidentForAssign}
          open={userPickerOpen}
          onClose={handleUserPickerClose}
          onAssignComplete={handleAssignComplete}
        />
      </div>
    );
  }

  // Wall-screen mode (simplified, no filters)
  if (incidents.length === 0) {
    return (
      <Text align="center" block size={600}>
        {t('statusPanel.noIncidents')}
      </Text>
    );
  }

  return (
    <div className={mergeClasses(styles.container, styles.wallScreenContainer)}>
      <div className={styles.tableScrollHost}>
        <Table aria-label={t('statusPanel.incidents')} size={tableSize} className={styles.tableBase}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.severity'),
                  t('statusPanel.columnTooltips.severity'),
                )}
              </TableHeaderCell>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.title'),
                  t('statusPanel.columnTooltips.title'),
                )}
              </TableHeaderCell>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.customer'),
                  t('statusPanel.columnTooltips.customer'),
                )}
              </TableHeaderCell>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.owner'),
                  t('statusPanel.columnTooltips.owner'),
                )}
              </TableHeaderCell>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.created'),
                  t('statusPanel.columnTooltips.created'),
                )}
              </TableHeaderCell>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.sla'),
                  t('statusPanel.columnTooltips.sla'),
                )}
              </TableHeaderCell>
              <TableHeaderCell className={styles.wallScreenHeaderCell}>
                {renderHeaderLabel(
                  t('statusPanel.columns.actions'),
                  t('statusPanel.columnTooltips.actions'),
                )}
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {incidents.map((incident) => (
              <TableRow key={incident.incidentId} className={styles.wallScreenRow}>
                <TableCell>
                  <SeverityBadge severity={incident.severity} size="large" />
                </TableCell>
                <TableCell>
                  <TableCellLayout truncate>
                    <Text size={400}>{incident.title}</Text>
                    <IncidentTagBadges tags={incident.tags} />
                  </TableCellLayout>
                </TableCell>
                <TableCell>
                  <Text size={400}>{incident.customerId}</Text>
                </TableCell>
                <TableCell>
                  <Text size={400}>{incident.assignedOperatorName ?? '—'}</Text>
                </TableCell>
                <TableCell>
                  <Text size={400}>
                    {new Date(incident.createdTime).toLocaleString()}
                  </Text>
                </TableCell>
                <TableCell>
                  <SlaCountdown
                    slaDeadline={incident.slaDeadline}
                    severity={incident.severity}
                    incidentState={incident.state}
                    slaType={incident.slaType}
                    size="large"
                  />
                </TableCell>
                <TableCell>
                  <IncidentActions
                    incident={incident}
                    settings={settings}
                    onActionComplete={onRefresh}
                    onAssignClick={handleAssignClick}
                    size="medium"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* User Picker Dialog for assigning incidents */}
      <UserPickerDialog
        incident={selectedIncidentForAssign}
        open={userPickerOpen}
        onClose={handleUserPickerClose}
        onAssignComplete={handleAssignComplete}
      />
    </div>
  );
};
